import * as Popover from "@radix-ui/react-popover";
import { useLocation, useNavigate } from "@tanstack/react-router";
import cn from "mcn";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Tooltip, TooltipTrigger } from "@artis/ui";
import { useLogout } from "../../context/auth/provider";
import { useUserSubscriptionTier } from "../../data/user";
import {
  MobileNavChartsIcon,
  MobileNavCurveIcon,
  MobileNavKebabIcon,
  MobileNavLogoutIcon,
  MobileNavMarketIcon,
} from "../icons/FooterNavIcons";

/** Below the vertical sidebar (`md` is 900px). */
export const MOBILE_NAV_MEDIA = "(max-width: 899px)";
/** Same height as the phone nav. Written out so `env()` is not nested inside another variable. */
export const mobileNavHeight = "calc(48px + env(safe-area-inset-bottom))";
const UPGRADE_TOOLTIP = "Please upgrade your subscription to access this feature";

export const mobileNavButtonClass = cn(
  "inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] border border-transparent bg-transparent p-[4px]",
  "cursor-pointer text-white",
  "hover:bg-white/10",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
  "disabled:pointer-events-none disabled:opacity-40",
);

export const mobileNavMenuPanelClass = cn(
  "z-[220] flex w-max min-w-[180px] max-w-[min(100vw-24px,280px)] flex-col",
  "border-none bg-transparent p-0 shadow-none outline-none",
);

/** Figma menu: 8px radius, 1px #e0e0e0 border, 4×8 padding, shadow 1px 4px 8px. */
export const mobileNavMenuSurfaceClass = cn(
  "flex w-max min-w-[120px] max-w-[min(100vw-24px,320px)] flex-col",
  "rounded-[8px] border border-solid border-[#e0e0e0] bg-white",
  "px-[4px] py-[8px]",
  "shadow-[1px_4px_8px_0px_rgba(0,0,0,0.15)]",
);

export const mobileNavMenuHeaderClass = "px-[4px] text-[12px] leading-[18px] font-medium text-[#4d4d4d]";

/** Figma menuItem: a 1px wrapper around an 8×4 padded state layer, so each row is 30px tall. */
export const mobileNavMenuItemClass = cn(
  "flex w-full min-w-[120px] cursor-pointer items-center gap-[8px] px-[8px] py-[4px] text-left",
  "border border-solid border-transparent bg-clip-padding bg-transparent",
  "text-[14px] leading-[20px] font-normal text-black outline-none",
  "rounded-[5px] hover:bg-neutral-tonal",
  "focus-visible:ring-2 focus-visible:ring-primary",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

type MobileNavActionsSlotContextValue = HTMLElement | null;
const MobileNavActionsSlotContext = createContext<MobileNavActionsSlotContextValue>(null);
const MobileNavActionsPresenceContext = createContext<(present: boolean) => void>(() => {});

export function MobileNavActionsProvider({
  slot,
  setPresent,
  children,
}: {
  slot: HTMLElement | null;
  setPresent: (present: boolean) => void;
  children: ReactNode;
}) {
  return (
    <MobileNavActionsSlotContext.Provider value={slot}>
      <MobileNavActionsPresenceContext.Provider value={setPresent}>{children}</MobileNavActionsPresenceContext.Provider>
    </MobileNavActionsSlotContext.Provider>
  );
}

/** Renders page-specific Actions into the mobile nav kebab slot. Hooks stay in the page tree. */
export function MobileNavActions({ children }: { children: ReactNode }) {
  const slot = useContext(MobileNavActionsSlotContext);
  const setPresent = useContext(MobileNavActionsPresenceContext);

  useEffect(() => {
    setPresent(true);
    return () => setPresent(false);
  }, [setPresent]);

  if (!slot) return null;
  return createPortal(children, slot);
}

function NavTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <TooltipTrigger placement="top">
      {children}
      <Tooltip>{label}</Tooltip>
    </TooltipTrigger>
  );
}

export function MobileNavBar({
  hideFromClassName,
  showDefaultActions,
  onActionsSlot,
}: {
  /** Tailwind class that hides the bar on wider screens, e.g. `md:hidden`. */
  hideFromClassName: string;
  showDefaultActions: boolean;
  onActionsSlot: (el: HTMLElement | null) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { disabledFeatures } = useUserSubscriptionTier();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [defaultMenuOpen, setDefaultMenuOpen] = useState(false);

  const onMarket = location.pathname.startsWith("/app/market");
  const onCharts = location.pathname.startsWith("/app/trading-chart");
  const onCurve = location.pathname.startsWith("/app/curve-management");

  const chartsDisabled = Boolean(disabledFeatures?.charts);
  const curveDisabled = Boolean(disabledFeatures?.createCurve && disabledFeatures?.editCurve);

  const handleLogout = useCallback(async () => {
    setLogoutLoading(true);
    try {
      await logout();
    } catch (error) {
      console.error("Error logging out:", error);
      setLogoutLoading(false);
    }
  }, [logout]);

  const go = (path: "/app/market" | "/app/trading-chart" | "/app/curve-management", alreadyThere: boolean) => {
    if (alreadyThere) return;
    navigate({ to: path });
  };

  return (
    <nav
      data-bottom-bar
      aria-label="Navigation"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[100] flex border-t border-solid border-[#101010] bg-[#050505]",
        "h-[calc(48px+env(safe-area-inset-bottom))] pt-[4px] pb-[calc(4px+env(safe-area-inset-bottom))]",
        hideFromClassName,
      )}
    >
      <div className="flex h-[40px] w-full items-center justify-between px-[12px]">
        <NavTooltip label="Log Out">
          <button
            type="button"
            className={mobileNavButtonClass}
            aria-label="Log Out"
            disabled={logoutLoading}
            onClick={handleLogout}
          >
            {logoutLoading ? (
              <span className="inline-flex h-[32px] w-[32px] items-center justify-center">
                <span className="h-[16px] w-[16px] animate-spin rounded-full border-2 border-current border-t-transparent" />
              </span>
            ) : (
              <MobileNavLogoutIcon />
            )}
          </button>
        </NavTooltip>

        <NavTooltip label="Market Sheet">
          <button
            type="button"
            className={mobileNavButtonClass}
            aria-label="Market Sheet"
            aria-current={onMarket ? "page" : undefined}
            onClick={() => go("/app/market", onMarket)}
          >
            <MobileNavMarketIcon />
          </button>
        </NavTooltip>

        <NavTooltip label={chartsDisabled ? UPGRADE_TOOLTIP : "Charts"}>
          <button
            type="button"
            className={mobileNavButtonClass}
            aria-label="Charts"
            aria-current={onCharts ? "page" : undefined}
            disabled={chartsDisabled}
            onClick={() => {
              if (!chartsDisabled) go("/app/trading-chart", onCharts);
            }}
          >
            <MobileNavChartsIcon />
          </button>
        </NavTooltip>

        <NavTooltip label={curveDisabled ? UPGRADE_TOOLTIP : "Curve Management"}>
          <button
            type="button"
            className={mobileNavButtonClass}
            aria-label="Curve Management"
            aria-current={onCurve ? "page" : undefined}
            disabled={curveDisabled}
            onClick={() => {
              if (!curveDisabled) go("/app/curve-management", onCurve);
            }}
          >
            <MobileNavCurveIcon />
          </button>
        </NavTooltip>

        {!showDefaultActions && <div ref={onActionsSlot} className="flex items-center" />}
        {showDefaultActions && (
          <Popover.Root open={defaultMenuOpen} onOpenChange={setDefaultMenuOpen}>
            <Popover.Trigger asChild>
              <button
                type="button"
                className={mobileNavButtonClass}
                aria-label="Actions"
                data-testid="mobile-actions-button"
              >
                <MobileNavKebabIcon />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content side="top" align="end" sideOffset={10} className={mobileNavMenuPanelClass}>
                <div className={mobileNavMenuSurfaceClass}>
                  <div className={mobileNavMenuHeaderClass}>Actions</div>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </div>
    </nav>
  );
}
